import './App.css';
import { useState, useEffect } from 'react';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import LuxonUtils from '@date-io/luxon';
import { DateTimePicker } from '@material-ui/pickers'
import { DateTime, Duration, Interval } from 'luxon';
import { Button, TextField, Select, MenuItem, FormControl, InputLabel, Paper, Radio, RadioGroup } from '@material-ui/core'
import { FormControlLabel, FormLabel } from '@material-ui/core'
import CssBaseline from '@material-ui/core/CssBaseline';
import RefreshIcon from '@material-ui/icons/Refresh';
import DeleteIcon from '@material-ui/icons/Delete';
import { makeStyles } from '@material-ui/core/styles';

function formatDuration(inputDuration) {
  const duration = Duration.fromObject({ seconds: inputDuration }).shiftTo("days", "hours", "minutes", "seconds");
  if (duration.days > 0) {
    return duration.toFormat("d 'days' h 'hours' m 'minutes' s 'seconds'")
  } else if (duration.hours > 0) {
    return duration.toFormat("h 'hours' m 'minutes' s 'seconds'")
  } else if (duration.minutes > 0) {
    return duration.toFormat("m 'minutes' s 'seconds'")
  } else {
    return duration.toFormat("s 'seconds'")
  }
  // const asMinutes = Math.trunc(durationSeconds / 60);
  // const asHours = Math.trunc(asMinutes / 60);
  // const days = Math.trunc(asHours / 24);

  // const hours = asHours - 24 * days;
  // const minutes = asMinutes - asHours * 60;
  // const seconds = durationSeconds - asMinutes * 60;

  // let result = `${seconds.toFixed(2)} seconds`;

  // if (minutes > 0) {
  //     result = `${minutes} minutes ${result}`;
  // }

  // if (hours > 0) {
  //     result = `${hours} hours ${result}`;
  // }

  // if (days > 0) {
  //     result = `${days} days ${result}`;
  // }

  // return result;
}

const useStyles = makeStyles((theme) => ({
  root: {
    '& > *': {
      margin: theme.spacing(1),
    },
    marginLeft: theme.spacing(4),
    marginRight: theme.spacing(4),
    marginTop: theme.spacing(4)
  },
  outputText: {
    fontSize: '1rem'
  }
}));

const appStyles = makeStyles((theme) => ({
  addTimerButton: {
    marginLeft: theme.spacing(4),
    marginRight: theme.spacing(4)
  }
}));

const LocalStorageName = 'timers';

function logAdjustment(x) {
  return x / Math.max(1, Math.log(x));
}

function logSquaredAdjustment(x) {
  if (x < (Math.E * Math.E)) {
    // This is the zero of the derivitive.
    return x;
  } else {
    const logVal = Math.log(x);
    return x / (logVal * logVal);
  }
}

function sqrtAdjustment(x) {
  return x > 0 ? Math.sqrt(x) : 0;
}

function identityAdjustment(x) {
  return Math.max(x, 0);
}

function Timer(props) {
  const classes = useStyles();

  const [startTime, setStartTime] = useState(props.defaultStartTime);
  const [currentTime, setCurrentTime] = useState(DateTime.local());

  const [granularity, setGranularity] = useState(props.defaultGranularity);
  const [divisor, setDivisor] = useState(props.defaultDivisor);
  const [divisorText, setDivisorText] = useState(divisor.toString());

  const [timerName, setTimerName] = useState(props.defaultTimerName);

  const [algorithmStr, setAlgorithmStr] = useState(props.defaultAlgorithmStr || logAdjustment.name);
  const algorithms = [logAdjustment, logSquaredAdjustment, sqrtAdjustment, identityAdjustment]
  const algorithm = algorithms.filter(alg => alg.name === algorithmStr).concat(logAdjustment.name)[0];

  const waitedInterval = Interval.fromDateTimes(startTime, currentTime);
  const waitedSeconds = waitedInterval.isValid ? waitedInterval.toDuration().shiftTo('seconds').seconds : 0;

  useEffect(() => {
    const timer = setTimeout(() => setCurrentTime(new Date()), 1000);
    return () => clearTimeout(timer);
  });

  useEffect(() => {
    props.onChange({
      startTime,
      granularity,
      divisor,
      timerName,
      algorithmStr
    });
  }, [startTime, granularity, divisor, timerName, algorithmStr]);

  useEffect(() => {
    try {
      const newDivisor = parseInt(divisorText);
      if (!isNaN(newDivisor) && isFinite(newDivisor) && newDivisor > 0) {
        setDivisor(newDivisor);
      } else {
        console.log('Not updating to invalid divisor [%o].', newDivisor);
      }
    } catch (err) {
      console.log('Not updating divisor to non-number [%o] due to exception. %o', divisorText, err);
    }
  }, [divisorText])

  let mult;
  switch (granularity) {
    case "seconds":
      mult = 1;
      break;
    case "minutes":
      mult = 60;
      break;
    case "hours":
      mult = 60 * 60;
      break;
    default:
      console.log(granularity);
      alert(`Error, invalid granularity: ${granularity}`);
  }

  const timeUnits = waitedSeconds / mult;
  const dividedUnits = timeUnits / divisor;
  const toWaitUnits = algorithm(dividedUnits);
  const toWaitSeconds = toWaitUnits * divisor * mult;

  const waitUntil = DateTime.local().plus(Duration.fromObject({ seconds: toWaitSeconds }));

  return (
    <Paper className={classes.root}>
      <TextField label="Timer name" value={timerName} onChange={e => setTimerName(e.target.value)} />
      <DateTimePicker label="Start time" value={startTime} onChange={setStartTime} disableFuture={true} openTo="hours" />
      {/* <input type="time" onChange={updateStartTime} value={dateToTimeInput(startTime)} /> */}
      <FormControl>
        <InputLabel>Granularity</InputLabel>
        <Select value={granularity} onChange={e => setGranularity(e.target.value)}>
          <MenuItem value="seconds">seconds</MenuItem>
          <MenuItem value="minutes">minutes</MenuItem>
          <MenuItem value="hours">hours</MenuItem>
        </Select>
      </FormControl>
      <TextField type="number" label="Divisor" value={divisorText} onChange={e => setDivisorText(e.target.value)} />
      <Button variant="contained" onClick={() => setStartTime(DateTime.local())} color="primary" startIcon={<RefreshIcon />}>Now</Button>
      <Button variant="contained" onClick={props.onClose} color="secondary" startIcon={<DeleteIcon />}>Delete</Button>
      <div>
        <FormControl component="fieldset">
          <FormLabel component="legend">Wait Algorithm</FormLabel>
          <RadioGroup row value={algorithmStr} onChange={e => setAlgorithmStr(e.target.value)}>
            <FormControlLabel value={logAdjustment.name} control={<Radio color="primary" />} label="Log" />
            <FormControlLabel value={logSquaredAdjustment.name} control={<Radio color="primary" />} label="Log²" />
            <FormControlLabel value={sqrtAdjustment.name} control={<Radio color="primary" />} label="Square Root" />
            <FormControlLabel value={identityAdjustment.name} control={<Radio color="primary" />} label="Identity" />
          </RadioGroup>
        </FormControl>
      </div>
      <div className={classes.outputText}>
        <p>Time Waited: {formatDuration(waitedSeconds)}</p>
        <p>Time to Wait: {formatDuration(toWaitSeconds)}</p>
        <p>Wait Until: {waitUntil.toLocaleString(DateTime.DATETIME_MED)}</p>
      </div>
    </Paper>
  );
}

function App() {
  const classes = appStyles();

  const defaultTimerState = () => {
    return {
      defaultStartTime: DateTime.local(),
      defaultGranularity: 'minutes',
      defaultDivisor: 1,
      defaultTimerName: ''
    };
  };

  const [appState, setAppState] = useState(() => {
    const initState = localStorage.getItem(LocalStorageName);
    if (initState) {
      const serialAppState = JSON.parse(initState);
      for (let [timerId, timerState] of Object.entries(serialAppState.timers)) {
        serialAppState.timers[timerId] = {
          ...timerState,
          defaultStartTime: DateTime.fromISO(timerState.defaultStartTime)
        }
      }

      return serialAppState;
    } else {
      return {
        timers: {
          0: defaultTimerState()
        }
      };
    }
  });
  useEffect(() => {
    const serialAppState = {
      ...appState
    }
    serialAppState.timers = {};
    for (let [timerId, timerState] of Object.entries(appState.timers)) {
      serialAppState.timers[timerId] = {
        ...timerState,
        defaultStartTime: DateTime.fromISO(timerState.defaultStartTime)
      }
    }
    localStorage.setItem(LocalStorageName, JSON.stringify(serialAppState));
  }, [appState]);

  const onTimerChange = timerId => timerState => {
    const newDefault = {
      defaultStartTime: timerState.startTime,
      defaultGranularity: timerState.granularity,
      defaultDivisor: timerState.divisor,
      defaultTimerName: timerState.timerName,
      defaultAlgorithmStr: timerState.algorithmStr
    };
    const newState = {
      ...appState,
      timers: {
        ...appState.timers,
        [timerId]: newDefault
      }
    };
    setAppState(newState);
  };

  const nextTimerId = Math.max(...Object.keys(appState.timers).map((num) => parseInt(num)).concat(-1)) + 1;

  const addTimer = () => {
    const newTimer = defaultTimerState();
    const newState = {
      ...appState,
      timers: {
        ...appState.timers,
        [nextTimerId]: newTimer
      }
    };
    setAppState(newState);
  };

  const removeTimerId = timerId => () => {
    const newState = {
      ...appState,
      timers: {
        ...appState.timers
      }
    }
    delete newState.timers[timerId];
    setAppState(newState);
  };

  const timers = Object.entries(appState.timers).map(([timerId, timerState]) => {
    return (<Timer onChange={onTimerChange(timerId)} onClose={removeTimerId(timerId)} key={timerId} {...timerState} />);
  });

  return (
    <div>
      <CssBaseline />
      <MuiPickersUtilsProvider utils={LuxonUtils}>
        {timers}
        <Button variant="contained" color="primary" onClick={addTimer} className={classes.addTimerButton}>Add Timer</Button>
      </MuiPickersUtilsProvider>
    </div>
  );
}

export default App;
